import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Loader2, Minus, Plus } from "lucide-react";

const withCurrent = (list, val) => (val && !list.includes(val) ? [val, ...list] : list);

export const SurveyEditDialog = ({ survey, onClose, onSaved }) => {
  const [form, setForm] = useState(null);
  const [options, setOptions] = useState({});
  const [products, setProducts] = useState(["None"]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/survey-options").then((r) => setOptions(r.data || {})).catch(() => {});
    api
      .get("/admin/skus")
      .then((r) => setProducts(["None", ...(r.data || []).map((s) => s.name)]))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!survey) {
      setForm(null);
      return;
    }
    setForm({
      customer_nationality: survey.customer_nationality || "",
      heard_about_ubbs: survey.heard_about_ubbs || "",
      current_product: survey.current_product || "",
      ubbs_products_bought: [...(survey.ubbs_products_bought || [])],
      ubbs_products_qty: { ...(survey.ubbs_products_qty || {}) },
      nicotine_level: survey.nicotine_level || "",
      flavor: survey.flavor || "",
      feedback: survey.feedback || "",
    });
  }, [survey]);

  if (!survey || !form) return null;

  const productList = [
    ...products,
    ...form.ubbs_products_bought.filter((p) => !products.includes(p)),
  ];

  const toggleProduct = (p) => {
    setForm((f) => {
      const exists = f.ubbs_products_bought.includes(p);
      if (p === "None") {
        return { ...f, ubbs_products_bought: exists ? [] : ["None"], ubbs_products_qty: {} };
      }
      const without = f.ubbs_products_bought.filter((x) => x !== "None");
      const qty = { ...f.ubbs_products_qty };
      if (exists) {
        delete qty[p];
        return {
          ...f,
          ubbs_products_bought: without.filter((x) => x !== p),
          ubbs_products_qty: qty,
        };
      }
      qty[p] = 1;
      return { ...f, ubbs_products_bought: [...without, p], ubbs_products_qty: qty };
    });
  };

  const changeQty = (p, delta) => {
    setForm((f) => ({
      ...f,
      ubbs_products_qty: {
        ...f.ubbs_products_qty,
        [p]: Math.max(1, (parseInt(f.ubbs_products_qty[p], 10) || 1) + delta),
      },
    }));
  };

  const save = async () => {
    if (!form.ubbs_products_bought.length)
      return toast.error("Select at least one product (or 'None')");
    setBusy(true);
    try {
      await api.patch(`/admin/surveys/${survey.id}`, form);
      toast.success("Survey updated");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const selectField = (label, key, list, testid) => (
    <div>
      <Label className="text-xs uppercase tracking-wider">{label}</Label>
      <Select value={form[key]} onValueChange={(v) => setForm((f) => ({ ...f, [key]: v }))}>
        <SelectTrigger className="rounded-xl mt-1" data-testid={testid}>
          <SelectValue placeholder="Select…" />
        </SelectTrigger>
        <SelectContent>
          {withCurrent(list || [], form[key]).map((n) => (
            <SelectItem key={n} value={n}>
              {n}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-xl max-w-lg max-h-[90vh] overflow-y-auto" data-testid="survey-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Edit survey</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {selectField("Nationality", "customer_nationality", options.nationalities, "edit-survey-nationality")}
          {selectField("Heard about UBBS", "heard_about_ubbs", options.heard_about_ubbs, "edit-survey-heard")}
          {selectField("Current product", "current_product", options.current_products, "edit-survey-current")}
          <div>
            <Label className="text-xs uppercase tracking-wider">UBBS products bought</Label>
            <div className="glass-panel mt-1 divide-y divide-slate-200/50 max-h-64 overflow-y-auto" data-testid="edit-survey-products">
              {productList.map((p) => {
                const checked = form.ubbs_products_bought.includes(p);
                return (
                  <div key={p} className="flex items-center gap-3 p-2.5">
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggleProduct(p)}
                      className="rounded-xl"
                      data-testid={`edit-survey-product-${p}`}
                    />
                    <span
                      className={`text-sm flex-1 cursor-pointer ${p === "None" ? "italic text-neutral-500" : ""}`}
                      onClick={() => toggleProduct(p)}
                    >
                      {p}
                    </span>
                    {checked && p !== "None" && (
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => changeQty(p, -1)}
                          className="w-7 h-7 border border-slate-300/70 flex items-center justify-center hover:bg-white/80"
                          data-testid={`edit-survey-qty-minus-${p}`}
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="w-8 text-center font-mono font-semibold text-sm" data-testid={`edit-survey-qty-${p}`}>
                          {form.ubbs_products_qty[p] || 1}
                        </span>
                        <button
                          type="button"
                          onClick={() => changeQty(p, 1)}
                          className="w-7 h-7 border border-slate-300/70 flex items-center justify-center hover:bg-white/80"
                          data-testid={`edit-survey-qty-plus-${p}`}
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          {selectField("Nicotine level", "nicotine_level", options.nicotine_levels, "edit-survey-nicotine")}
          <div>
            <Label className="text-xs uppercase tracking-wider">Flavor</Label>
            <Input
              className="rounded-xl mt-1"
              value={form.flavor}
              onChange={(e) => setForm((f) => ({ ...f, flavor: e.target.value }))}
              data-testid="edit-survey-flavor"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Feedback</Label>
            <Textarea
              className="rounded-xl mt-1"
              value={form.feedback}
              onChange={(e) => setForm((f) => ({ ...f, feedback: e.target.value }))}
              data-testid="edit-survey-feedback"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-xl" onClick={onClose} data-testid="edit-survey-cancel">
            Cancel
          </Button>
          <Button
            className="rounded-xl bg-slate-900 text-white hover:bg-slate-700"
            onClick={save}
            disabled={busy}
            data-testid="edit-survey-save"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
